import type { Asset, AssetVersion } from "@agentsaas/protocol";
import type { z } from "zod";
import { modelFormat } from "./viewer-model";

/** 资产库的纯逻辑：切片网格、缩放、版本对比、上传类型推断 */

export type Version = z.infer<typeof AssetVersion>;
export type AssetKind = Asset["kind"];
export const ASSET_KINDS: AssetKind[] = ["image", "sprite", "model3d", "audio", "other"];

export const isImageLike = (kind: AssetKind, mediaType?: string) => kind === "image" || kind === "sprite" || (!!mediaType && mediaType.startsWith("image/"));
export const isModel = (kind: AssetKind, mediaType?: string) => kind === "model3d" || modelFormat(mediaType) !== null;

export interface Grid {
  cols: number;
  rows: number;
}

const toInt = (v: unknown) => {
  const n = typeof v === "number" ? v : typeof v === "string" ? Number(v) : NaN;
  return Number.isInteger(n) && n > 0 && n <= 64 ? n : null;
};

/** 切片网格的默认值：版本参数里的 cols/rows（或 grid:"4x2"）；精灵图缺省 4×4；其他图片默认不叠加 */
export function gridFromParams(kind: AssetKind, params: Record<string, unknown>): Grid | null {
  const cols = toInt(params.cols ?? params.columns);
  const rows = toInt(params.rows);
  if (cols && rows) return { cols, rows };
  if (typeof params.grid === "string") {
    const m = /^(\d+)\s*[x×]\s*(\d+)$/i.exec(params.grid.trim());
    const c = m ? toInt(m[1]) : null;
    const r = m ? toInt(m[2]) : null;
    if (c && r) return { cols: c, rows: r };
  }
  return kind === "sprite" ? { cols: 4, rows: 4 } : null;
}

export const ZOOM_MIN = 0.1;
export const ZOOM_MAX = 16;
export const clampZoom = (z: number) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(z * 1000) / 1000));

/** 以 (cx, cy)（相对视口左上角）为中心缩放：返回新的平移量，使该点在缩放前后不动 */
export function zoomAt(view: { x: number; y: number; z: number }, nextZ: number, cx: number, cy: number) {
  const z = clampZoom(nextZ);
  const k = z / view.z;
  return { z, x: cx - (cx - view.x) * k, y: cy - (cy - view.y) * k };
}

/** 适配：让图片完整显示在视口里并居中（不放大超过 1） */
export function fitView(img: { w: number; h: number }, box: { w: number; h: number }) {
  if (!img.w || !img.h || !box.w || !box.h) return { x: 0, y: 0, z: 1 };
  const z = clampZoom(Math.min(1, box.w / img.w, box.h / img.h));
  return { z, x: (box.w - img.w * z) / 2, y: (box.h - img.h * z) / 2 };
}

/** 版本对比的字段行：changed = 两边不同 */
export interface DiffRow {
  field: "prompt" | "provider" | "cost" | "size" | "mediaType" | "params" | "createdBy" | "derivedFrom" | ModelField;
  a: string;
  b: string;
  changed: boolean;
}

const show = (v: unknown) => (v === null || v === undefined || v === "" ? "—" : typeof v === "string" ? v : JSON.stringify(v));

/** 模型指标的对比行（两边任一有 AssetVersion.model 时出现） */
export type ModelField = "tris" | "verts" | "textures" | "animations" | "joints" | "bbox";

function modelRows(a: Version, b: Version): [ModelField, string, string][] {
  if (!a.model && !b.model) return [];
  const cell = (v: Version, f: ModelField): string => {
    const m = v.model;
    if (!m) return "—";
    switch (f) {
      case "tris":
        return String(m.tris);
      case "verts":
        return String(m.verts);
      case "textures":
        return m.textures.length ? m.textures.map((x) => `${x.width}×${x.height}`).join(", ") : "—";
      case "animations":
        return m.animations.length ? m.animations.join(", ") : "—";
      case "joints":
        return m.skinned ? String(m.joints) : "—";
      case "bbox":
        return `${m.bbox.x} × ${m.bbox.y} × ${m.bbox.z}`;
    }
  };
  return (["tris", "verts", "textures", "animations", "joints", "bbox"] as const).map((f) => [f, cell(a, f), cell(b, f)]);
}

export function versionDiff(a: Version, b: Version, fmt: { cost(n: number): string; size(n: number): string; actor(v: Version["createdBy"]): string }): DiffRow[] {
  const rows: [DiffRow["field"], string, string][] = [
    ["prompt", show(a.prompt), show(b.prompt)],
    ["provider", show(a.provider), show(b.provider)],
    ["cost", fmt.cost(a.costMicroUsd), fmt.cost(b.costMicroUsd)],
    ["size", fmt.size(a.size), fmt.size(b.size)],
    ["mediaType", a.mediaType, b.mediaType],
    ["params", show(Object.keys(a.params).length ? a.params : null), show(Object.keys(b.params).length ? b.params : null)],
    ["createdBy", fmt.actor(a.createdBy), fmt.actor(b.createdBy)],
    ["derivedFrom", a.derivedFrom ? `v${a.derivedFrom.version}` : "—", b.derivedFrom ? `v${b.derivedFrom.version}` : "—"],
    ...modelRows(a, b),
  ];
  return rows.map(([field, x, y]) => ({ field, a: x, b: y, changed: x !== y }));
}

/** 派生链路里一个节点的阶段：版本参数里的 op（remesh / rig / animate …），否则按资产类型 */
export function stageOf(kind: AssetKind, version: Pick<Version, "params">): { op: string } | { kind: AssetKind } {
  const op = version.params.op;
  return typeof op === "string" && ["generate", "remesh", "retexture", "rig", "animate", "convert"].includes(op) ? { op } : { kind };
}

/** 上传：由文件推断资产类型与媒体类型 */
export function uploadKindFor(file: { name: string; type: string }): { kind: AssetKind; mediaType: string } {
  const lower = file.name.toLowerCase();
  if (lower.endsWith(".glb")) return { kind: "model3d", mediaType: "model/gltf-binary" };
  if (lower.endsWith(".gltf")) return { kind: "model3d", mediaType: "model/gltf+json" };
  if (lower.endsWith(".fbx")) return { kind: "model3d", mediaType: "model/fbx" };
  if (lower.endsWith(".obj")) return { kind: "model3d", mediaType: "model/obj" };
  const type = file.type || "application/octet-stream";
  if (type.startsWith("image/")) return { kind: "image", mediaType: type };
  if (type.startsWith("audio/")) return { kind: "audio", mediaType: type };
  return { kind: "other", mediaType: type };
}

/** "a, b ,,c" → ["a","b","c"]（去重） */
export function parseTags(s: string): string[] {
  return [...new Set(s.split(/[,，]/).map((x) => x.trim()).filter(Boolean))].slice(0, 20);
}

/** 默认通知谁：该版本的生成者（agent）；否则项目里的美术 / 3D 建模师 agent */
export function defaultNotify(version: Version | undefined, agents: { id: string; role: string }[]): string[] {
  if (version?.createdBy.type === "agent") return [version.createdBy.id];
  return agents.filter((a) => a.role === "art" || a.role === "modeler").map((a) => a.id);
}
